import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Toast from '../Toast';
import styles from './Ranking.module.css';

interface MercenaryInfo {
  id: number;
  name: string;
  level: number;
  race: string;
}

interface StatEntry {
  mercenaryId: number;
  mercenary: MercenaryInfo | null;
  [key: string]: any;
}

interface ReputationEntry {
  id: number;
  name: string;
  reputation: number;
}

interface CombatLeaderboardsData {
  maxDamageAttack: StatEntry[];
  maxDamageMatch: StatEntry[];
  maxDodges: StatEntry[];
  maxBlocks: StatEntry[];
  tournamentWins: StatEntry[];
  mostCriticalHits: StatEntry[];
  mostKills: StatEntry[];
  mostHospitalizations: StatEntry[];
  highestReputation: ReputationEntry[];
  lowestReputation: ReputationEntry[];
}

const CombatLeaderboards: React.FC = () => {
  const [data, setData] = useState<CombatLeaderboardsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Function to pad entries to exactly 5 items with placeholders
  const padEntries = (entries: StatEntry[], statKey: string): StatEntry[] => {
    const paddedEntries = [...entries];
    while (paddedEntries.length < 5) {
      paddedEntries.push({
        mercenaryId: -1,
        mercenary: null,
        [statKey]: 0
      });
    }
    return paddedEntries;
  };

  // Function to pad reputation entries to exactly 5 items with placeholders
  const padReputationEntries = (entries: ReputationEntry[]): ReputationEntry[] => {
    const paddedEntries = [...entries];
    while (paddedEntries.length < 5) {
      paddedEntries.push({
        id: -1,
        name: '---',
        reputation: 0
      });
    }
    return paddedEntries;
  };
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  useEffect(() => {
    fetchCombatLeaderboards();
  }, []);

  const fetchCombatLeaderboards = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/mercenary/combat-leaderboards', {
        withCredentials: true,
      });

      setData(response.data);
    } catch (error) {
      console.error('Error fetching combat leaderboards:', error);
      displayToast('Failed to load combat leaderboards. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderStatSection = (title: string, entries: StatEntry[], statKey: string) => {
    const paddedEntries = padEntries(entries, statKey);

    return (
      <div className={styles.section}>
        <h3>{title}</h3>
        <div className={styles.leaderboardList}>
          {paddedEntries.map((entry, index) => (
            <div key={`${entry.mercenaryId}-${index}`} className={styles.leaderboardEntry}>
              <span className={styles.rank}>#{index + 1}</span>
              <div className={styles.mercenaryDetails}>
                {entry.mercenary ? (
                  <a
                    href={`/mercenary/${entry.mercenary.id}`}
                    className={styles.mercenaryName}
                  >
                    {entry.mercenary.name}
                  </a>
                ) : (
                  <span className={styles.placeholderName}>---</span>
                )}
                {entry.mercenary && (
                  <span className={styles.mercenaryInfo}>
                    Level {entry.mercenary.level} {entry.mercenary.race}
                  </span>
                )}
              </div>
              <span className={styles.statValue}>
                {entry[statKey] || '---'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReputationSection = (title: string, entries: ReputationEntry[], isPositive: boolean) => {
    const paddedEntries = padReputationEntries(entries);

    return (
      <div className={styles.section}>
        <h3>{title}</h3>
        <div className={styles.leaderboardList}>
          {paddedEntries.map((entry, index) => (
            <div key={`${entry.id}-${index}`} className={styles.leaderboardEntry}>
              <span className={styles.rank}>#{index + 1}</span>
              <div className={styles.mercenaryDetails}>
                {entry.id !== -1 ? (
                  <a
                    href={`/mercenary/${entry.id}`}
                    className={styles.mercenaryName}
                  >
                    {entry.name}
                  </a>
                ) : (
                  <span className={styles.placeholderName}>---</span>
                )}
              </div>
              <span className={`${styles.statValue} ${isPositive ? styles.positive : styles.negative}`}>
                {entry.id !== -1 ? entry.reputation : '---'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <h2>Combat Leaderboards</h2>
        <div className={styles.loading}>Loading combat statistics...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <h2>Combat Leaderboards</h2>
        <div className={styles.error}>Failed to load combat leaderboards.</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2>Combat Leaderboards</h2>
      <p className={styles.description}>
        Discover the most skilled warriors in Njordheim through their combat achievements.
      </p>

      <div className={styles.leaderboardGrid}>
        {renderStatSection(
          'Highest Single Attack Damage',
          data.maxDamageAttack,
          'highestDamageDealt'
        )}

        {renderStatSection(
          'Most Damage in One Battle',
          data.maxDamageMatch,
          'mostDamageDone'
        )}

        {renderStatSection(
          'Most Dodges in One Battle',
          data.maxDodges,
          'mostDodgedAttacks'
        )}

        {renderStatSection(
          'Most Blocks in One Battle',
          data.maxBlocks,
          'mostBlockedAttacks'
        )}

        {renderStatSection(
          'Most Tournament Victories',
          data.tournamentWins,
          'winsTournament'
        )}

        {renderStatSection(
          'Most Critical Hits in One Battle',
          data.mostCriticalHits,
          'mostCriticalHits'
        )}

        {renderStatSection(
          'Most Kills',
          data.mostKills,
          'kills'
        )}

        {renderStatSection(
          'Most Hospitalizations',
          data.mostHospitalizations,
          'hospitalizations'
        )}

        {renderReputationSection(
          'Highest Reputation',
          data.highestReputation,
          true
        )}

        {renderReputationSection(
          'Most Infamous',
          data.lowestReputation,
          false
        )}
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  );
};

export default CombatLeaderboards;
